import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import AppBar from "@mui/material/AppBar";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import MDAlert from "components/MDAlert";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import XemChungTuTrongApp from "components/XemChungTuTrongApp";
import { oTieuDeCot, AN_DUOI_MD, chuXuongDong } from "lib/kieuBangDuLieu";
import { SEMANTIC } from "assets/theme/avocadoBrand";
import {
  useDialogFullManHinh,
  oDialogContentFormDai,
  oDialogPaperFormDai,
} from "lib/dungDialogFullManHinh";
import { giaoDichApi } from "api/giaoDich";
import { nhanTrangThaiThanhToan } from "lib/nhanTrangThaiThanhToan";

function homNayIso() {
  return new Date().toISOString().slice(0, 10);
}

function congNgayIso(iso, delta) {
  const d = new Date(`${iso}T12:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function ngayGio(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function tongTienPo(po) {
  return (po.items || []).reduce((s, it) => s + (it.thanhTien || 0), 0);
}

function PhuHangCho({ po, tongTien, them }) {
  const phan = [
    ngayGio(po.thoiGian),
    po.tenNhanVien,
    `${tongTien.toLocaleString("vi-VN")} đ`,
    nhanTrangThaiThanhToan(po.trangThaiThanhToan).label,
    ...(them || []),
  ].filter(Boolean);
  return (
    <MDTypography
      variant="caption"
      color="text"
      display={{ xs: "block", md: "none" }}
      mt={0.25}
      lineHeight={1.4}
      sx={chuXuongDong}
    >
      {phan.join(" · ")}
    </MDTypography>
  );
}

PhuHangCho.propTypes = {
  po: PropTypes.shape({
    thoiGian: PropTypes.string,
    tenNhanVien: PropTypes.string,
    trangThaiThanhToan: PropTypes.string,
  }).isRequired,
  tongTien: PropTypes.number.isRequired,
  them: PropTypes.arrayOf(PropTypes.string),
};

PhuHangCho.defaultProps = {
  them: undefined,
};

function BangPhieu({ danhSach, nutNhan, emptyIcon, emptyTieuDe, emptyMoTa, cotThem, onMoChiTiet }) {
  if (danhSach.length === 0) {
    return (
      <MDBox px={2.5} pb={4} pt={1} textAlign="center">
        <Icon sx={{ color: SEMANTIC.textDisabled, fontSize: "2.5rem" }}>{emptyIcon}</Icon>
        <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
          {emptyTieuDe}
        </MDTypography>
        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
          {emptyMoTa}
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox sx={{ overflowX: "auto" }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={oTieuDeCot}>Chủ vườn</TableCell>
            <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Thu mua</TableCell>
            {cotThem === "thanhToan" && (
              <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Thanh toán</TableCell>
            )}
            <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>NV thu mua</TableCell>
            <TableCell align="right" sx={[oTieuDeCot, AN_DUOI_MD]}>
              Tổng tiền
            </TableCell>
            <TableCell sx={oTieuDeCot}>Trạng thái</TableCell>
            <TableCell align="right" sx={oTieuDeCot}>
              Thao tác
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {danhSach.map((po) => {
            const tongTien = tongTienPo(po);
            const tt = nhanTrangThaiThanhToan(po.trangThaiThanhToan);
            return (
              <TableRow
                key={po.id}
                hover
                onClick={() => onMoChiTiet(po)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>
                  <MDTypography variant="button" fontWeight="medium" display="block">
                    {po.tenChuVuon ?? "—"}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block" sx={chuXuongDong}>
                    {po.id.slice(0, 8)}…
                  </MDTypography>
                  <PhuHangCho
                    po={po}
                    tongTien={tongTien}
                    them={
                      cotThem === "thanhToan" ? [`TT: ${ngayGio(po.thoiGianThanhToan)}`] : undefined
                    }
                  />
                </TableCell>
                <TableCell sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}>
                  {ngayGio(po.thoiGian)}
                </TableCell>
                {cotThem === "thanhToan" && (
                  <TableCell sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}>
                    {ngayGio(po.thoiGianThanhToan)}
                  </TableCell>
                )}
                <TableCell sx={AN_DUOI_MD}>{po.tenNhanVien ?? "—"}</TableCell>
                <TableCell align="right" sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}>
                  {tongTien.toLocaleString("vi-VN")} đ
                </TableCell>
                <TableCell>
                  <MDBadge badgeContent={tt.label} color={tt.color} variant="gradient" size="sm" />
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <MDButton
                    variant="text"
                    color="info"
                    size="small"
                    onClick={() => onMoChiTiet(po)}
                  >
                    {nutNhan}
                  </MDButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </MDBox>
  );
}

BangPhieu.propTypes = {
  danhSach: PropTypes.arrayOf(PropTypes.object).isRequired,
  nutNhan: PropTypes.string.isRequired,
  emptyIcon: PropTypes.string.isRequired,
  emptyTieuDe: PropTypes.string.isRequired,
  emptyMoTa: PropTypes.string.isRequired,
  cotThem: PropTypes.oneOf(["thanhToan"]),
  onMoChiTiet: PropTypes.func.isRequired,
};

BangPhieu.defaultProps = {
  cotThem: undefined,
};

function HangChoThanhToan() {
  const navigate = useNavigate();
  const fullManHinh = useDialogFullManHinh();
  const [tab, setTab] = useState(0);
  const [danhSachCho, setDanhSachCho] = useState([]);
  const [danhSachDaTt, setDanhSachDaTt] = useState([]);
  const [dangTaiCho, setDangTaiCho] = useState(true);
  const [dangTaiDaTt, setDangTaiDaTt] = useState(false);
  const [loi, setLoi] = useState("");
  const [xemDaTt, setXemDaTt] = useState(null);

  const macDinhDen = useMemo(() => homNayIso(), []);
  const macDinhTu = useMemo(() => congNgayIso(macDinhDen, -29), [macDinhDen]);
  const [tuNgay, setTuNgay] = useState(macDinhTu);
  const [denNgay, setDenNgay] = useState(macDinhDen);

  useEffect(() => {
    setDangTaiCho(true);
    setLoi("");
    const huy = giaoDichApi.langNgheHangChoKeToan(
      (ds) => {
        setDanhSachCho(ds);
        setDangTaiCho(false);
      },
      (err) => {
        setLoi(err.message);
        setDangTaiCho(false);
      }
    );
    return huy;
  }, []);

  useEffect(() => {
    if (tab !== 1) return undefined;
    if (tuNgay > denNgay) {
      setLoi("Từ ngày phải trước hoặc bằng Đến ngày.");
      return undefined;
    }
    setDangTaiDaTt(true);
    setLoi("");
    const huy = giaoDichApi.langNgheDaThanhToan(
      tuNgay,
      denNgay,
      (ds) => {
        setDanhSachDaTt(ds);
        setDangTaiDaTt(false);
        setXemDaTt((cur) => {
          if (!cur) return cur;
          return ds.find((po) => po.id === cur.id) || cur;
        });
      },
      (err) => {
        setLoi(err.message);
        setDangTaiDaTt(false);
      }
    );
    return huy;
  }, [tab, tuNgay, denNgay]);

  function moXuLyCho(po) {
    navigate(`/bao-cao/hang-cho-thanh-toan/phieu/${po.id}`);
  }

  const ttXem = xemDaTt ? nhanTrangThaiThanhToan(xemDaTt.trangThaiThanhToan) : null;
  const tongXem = xemDaTt ? tongTienPo(xemDaTt) : 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Báo cáo"
          tieuDe="Hàng chờ thanh toán"
          moTa="Phiếu chờ xử lý và lịch sử đã thanh toán — cập nhật realtime."
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ color: SEMANTIC.info, fontSize: "1.1rem" }}>sync</Icon>
            <MDTypography variant="caption" color="text">
              Đang lắng nghe realtime
            </MDTypography>
          </MDBox>
        </DauTrang>

        {loi && (
          <MDBox mb={2}>
            <MDAlert color="error">{loi}</MDAlert>
          </MDBox>
        )}

        <AppBar position="static" sx={{ mb: 2 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab
              label={`Chờ xử lý (${danhSachCho.length})`}
              icon={
                <Icon fontSize="small" sx={{ mt: -0.25 }}>
                  pending_actions
                </Icon>
              }
            />
            <Tab
              label="Đã thanh toán"
              icon={
                <Icon fontSize="small" sx={{ mt: -0.25 }}>
                  check_circle
                </Icon>
              }
            />
          </Tabs>
        </AppBar>

        {tab === 1 && (
          <Card sx={{ mb: 2, p: 2 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={4} md={3}>
                <MDInput
                  type="date"
                  label="Từ ngày"
                  value={tuNgay}
                  onChange={(e) => setTuNgay(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <MDInput
                  type="date"
                  label="Đến ngày"
                  value={denNgay}
                  onChange={(e) => setDenNgay(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={6}>
                <MDTypography variant="caption" color="text">
                  Lọc theo ngày thanh toán (mặc định 30 ngày gần nhất). Bấm dòng để xem popup.
                </MDTypography>
              </Grid>
            </Grid>
          </Card>
        )}

        <Card>
          <MDBox
            px={2.5}
            py={2}
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <MDTypography variant="h6" component="h2">
              {tab === 0 ? "Phiếu chờ xử lý" : "Lịch sử đã thanh toán"}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {tab === 0
                ? dangTaiCho
                  ? "Đang kết nối..."
                  : `${danhSachCho.length} phiếu`
                : dangTaiDaTt
                ? "Đang tải..."
                : `${danhSachDaTt.length} phiếu`}
            </MDTypography>
          </MDBox>

          {tab === 0 ? (
            dangTaiCho ? (
              <MDBox px={2.5} pb={3}>
                <MDTypography variant="button" color="text">
                  Đang tải hàng chờ...
                </MDTypography>
              </MDBox>
            ) : (
              <BangPhieu
                danhSach={danhSachCho}
                nutNhan="Xử lý"
                emptyIcon="inbox"
                emptyTieuDe="Không có phiếu trong hàng chờ"
                emptyMoTa="Khi nhân viên gửi phiếu về kế toán, chúng sẽ hiện ở đây ngay."
                onMoChiTiet={moXuLyCho}
              />
            )
          ) : dangTaiDaTt ? (
            <MDBox px={2.5} pb={3}>
              <MDTypography variant="button" color="text">
                Đang tải lịch sử đã thanh toán...
              </MDTypography>
            </MDBox>
          ) : (
            <BangPhieu
              danhSach={danhSachDaTt}
              nutNhan="Xem"
              emptyIcon="receipt_long"
              emptyTieuDe="Chưa có phiếu đã thanh toán trong khoảng ngày"
              emptyMoTa="Đổi khoảng ngày hoặc thanh toán phiếu ở tab Chờ xử lý."
              cotThem="thanhToan"
              onMoChiTiet={setXemDaTt}
            />
          )}
        </Card>
      </MDBox>
      <Footer />

      <Dialog
        open={!!xemDaTt}
        onClose={() => setXemDaTt(null)}
        fullScreen={fullManHinh}
        fullWidth
        maxWidth="md"
        PaperProps={oDialogPaperFormDai}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 6, flexShrink: 0 }}>
          <NhanDau>Lịch sử</NhanDau>
          <MDTypography variant="h5" component="h2">
            Chi tiết đã thanh toán
          </MDTypography>
          <IconButton
            onClick={() => setXemDaTt(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        {xemDaTt && (
          <DialogContent sx={oDialogContentFormDai}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text" textTransform="uppercase">
                  Chủ vườn
                </MDTypography>
                <MDTypography variant="button" fontWeight="medium" display="block">
                  {xemDaTt.tenChuVuon ?? "—"}
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text" textTransform="uppercase">
                  NV thu mua
                </MDTypography>
                <MDTypography variant="button" fontWeight="medium" display="block">
                  {xemDaTt.tenNhanVien ?? "—"}
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text" textTransform="uppercase">
                  Thời gian thu mua
                </MDTypography>
                <MDTypography variant="button" display="block">
                  {ngayGio(xemDaTt.thoiGian)}
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text" textTransform="uppercase">
                  Thời gian thanh toán
                </MDTypography>
                <MDTypography variant="button" display="block">
                  {ngayGio(xemDaTt.thoiGianThanhToan)}
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text" textTransform="uppercase">
                  Tổng tiền
                </MDTypography>
                <MDTypography variant="button" fontWeight="bold" display="block">
                  {tongXem.toLocaleString("vi-VN")} đ
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text" textTransform="uppercase">
                  Trạng thái
                </MDTypography>
                <MDBox mt={0.5}>
                  <MDBadge
                    badgeContent={ttXem.label}
                    color={ttXem.color}
                    variant="gradient"
                    size="sm"
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDTypography variant="caption" color="text" textTransform="uppercase">
                  Chi tiết loại bơ
                </MDTypography>
                {(xemDaTt.items || []).map((it, idx) => (
                  <MDBox
                    key={`${it.maLoaiBo}-${idx}`}
                    display="flex"
                    justifyContent="space-between"
                    gap={1}
                    py={0.5}
                  >
                    <MDTypography variant="button">
                      {it.tenLoaiBo ?? "—"} · {Number(it.khoiLuong || 0).toLocaleString("vi-VN")} kg
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="medium">
                      {Number(it.thanhTien || 0).toLocaleString("vi-VN")} đ
                    </MDTypography>
                  </MDBox>
                ))}
              </Grid>
              <Grid item xs={12}>
                <XemChungTuTrongApp url={xemDaTt.chungTuUrl} />
              </Grid>
              <Grid item xs={12}>
                <MDButton
                  component={Link}
                  to={`/bao-cao/hang-cho-thanh-toan/phieu/${xemDaTt.id}`}
                  variant="outlined"
                  color="dark"
                  size="small"
                  onClick={() => setXemDaTt(null)}
                >
                  Mở trang chi tiết đầy đủ
                </MDButton>
              </Grid>
            </Grid>
          </DialogContent>
        )}
      </Dialog>
    </DashboardLayout>
  );
}

export default HangChoThanhToan;
